import React, { useState, memo, useCallback } from 'react';
import { Input, Button, CenteredModal } from 'components';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'store';
import { deleteParticipants } from '../../../../store/Participant';
import { useToast } from '@app/core';
import { useDeletePanelParticipants } from 'queries';

interface Props {
  toDeleteParticipantsList: string[];
  panelId: string;
  isOpen: boolean;
  onRequestClose: () => any;
  onDeleteSuccess?: () => any;
}

export const DeleteParticipantsModal = memo(
  ({
    toDeleteParticipantsList,
    isOpen,
    onRequestClose,
    onDeleteSuccess,
    panelId,
  }: Props) => {
    const dispatch = useDispatch<ThunkDispatch>();
    const toast = useToast();
    const { t } = useTranslation();

    const { mutateAsync: deleteParticipantsMutation, isLoading } =
      useDeletePanelParticipants(panelId);

    const deleteText = t('participants.list.delete.confirmText');
    const [deleteError, setDeleteError] = useState(false);
    const [deleteField, setDeleteField] = useState('');

    const onDeleteFieldChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setDeleteField(e.target.value);
        setDeleteError(false);
      },
      [setDeleteField],
    );

    const onDeleteClick = useCallback(async () => {
      try {
        if (deleteField.toLowerCase() !== deleteText.toLowerCase()) {
          setDeleteError(true);
          return;
        }

        await deleteParticipantsMutation(toDeleteParticipantsList);
        dispatch(deleteParticipants(panelId, toDeleteParticipantsList)),
          setDeleteField('');

        if (onDeleteSuccess) onDeleteSuccess();
        toast.success(t('toast.deleted'));
      } catch (e) {
        toast.exception();
      }
      onRequestClose();
    }, [
      toDeleteParticipantsList,
      dispatch,
      toast,
      onRequestClose,
      onDeleteSuccess,
      t,
      deleteField,
      deleteText,
      setDeleteField,
      panelId,
    ]);

    return (
      <CenteredModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        title={t('participants.list.delete.title', {
          count: toDeleteParticipantsList.length,
        })}
        id="delete_participant"
        buttons={[
          <Button
            onClick={onRequestClose}
            color="ink"
            variant="transparent"
            key="cancel"
            small
          >
            <Trans i18nKey="participants.list.delete.cancel" />
          </Button>,
          <Button
            onClick={onDeleteClick}
            color="red"
            small
            key="delete"
            isLoading={isLoading}
            disabled={isLoading}
          >
            <Trans i18nKey="participants.list.delete.confirm" />
          </Button>,
        ]}
        text={t('participants.list.delete.text', {
          count: toDeleteParticipantsList.length,
        })}
      >
        <Input
          label={t('participants.list.delete.field', { text: deleteText })}
          onChange={onDeleteFieldChange}
          value={deleteField}
          error={
            deleteError
              ? t('participants.list.delete.error', { text: deleteText })
              : undefined
          }
        />
      </CenteredModal>
    );
  },
);
